import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Component
// ============================================================================
//
// The employer list is a single richtext token — authors edit a plain
// bullet list in the inline editor and add / remove employers by adding
// or removing list items. The list is styled as a multi-column grid so
// it looks tidy at any length.

export function PreferredEmployerList() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-12 md:py-20 bg-[#F5F6F7]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pep-list-header">
          <RichTextContent
            path="preferredemployerlist.heading"
            as="h2"
            className="pep-list-heading"
          />
        </div>

        <RichTextContent
          path="preferredemployerlist.employers"
          as="div"
          className="pep-list-employers"
        />

        <RichTextContent
          path="preferredemployerlist.disclaimer"
          as="p"
          className="pep-list-disclaimer"
        />
      </div>

      <style>{`
        .pep-list-header {
          text-align: center;
          max-width: 60rem;
          margin: 0 auto 2.5rem;
        }

        .pep-list-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.75rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          line-height: 1.15;
          letter-spacing: 0.01em;
          margin: 0 0 0.9rem;
        }
        @media (min-width: 768px) {
          .pep-list-heading {
            font-size: 2.25rem;
          }
        }
        .pep-list-heading p,
        .pep-list-heading span {
          font: inherit;
          color: inherit;
          margin: 0;
        }

        /* Bullet list — the RichText field emits <ul><li>. Break it across
           multiple columns on wider screens so a long list stays scannable. */
        .pep-list-employers {
          max-width: 60rem;
          margin: 0 auto;
          background: #fff;
          border: 1px solid var(--vibe-color-border);
          border-radius: 4px;
          padding: 1.75rem 1.75rem 1.5rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }

        .pep-list-employers ul {
          list-style: none;
          margin: 0;
          padding: 0;
          columns: 1;
          column-gap: 2rem;
        }
        @media (min-width: 640px) {
          .pep-list-employers ul {
            columns: 2;
          }
        }
        @media (min-width: 1024px) {
          .pep-list-employers ul {
            columns: 3;
          }
        }

        .pep-list-employers li {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          padding: 0.4rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          break-inside: avoid;
        }

        .pep-list-employers li::before {
          content: '';
          flex-shrink: 0;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--vibe-color-primary);
          margin-top: calc((1.5em - 6px) / 2);
        }

        .pep-list-employers a {
          color: inherit;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: color 0.2s ease, border-color 0.2s ease;
        }
        .pep-list-employers a:hover {
          color: var(--vibe-color-primary);
          border-bottom-color: var(--vibe-color-primary);
        }

        /* Fallback if an author leaves paragraphs instead of a list. */
        .pep-list-employers p {
          margin: 0 0 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
        }
        .pep-list-employers p:last-child { margin-bottom: 0; }

        .pep-list-disclaimer {
          margin: 2rem auto 0;
          max-width: 44rem;
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-style: italic;
          line-height: 1.5;
          color: var(--vibe-color-text-muted);
        }
        .pep-list-disclaimer p { margin: 0; }
        .pep-list-disclaimer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PreferredEmployerList',
  displayName: 'Preferred Employer — Employer List',
  tokens: {
    'preferredemployerlist.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Our Preferred Employers</p>',
    },
    'preferredemployerlist.employers': {
      label: 'Employer List',
      type: 'richtext',
      description:
        'Bullet list of preferred employers. Add or remove list items freely; wrap a name in a link to point to the employer\'s website.',
      defaultValue:
        '<ul><li>Local Hospital System</li><li>City School District</li><li>State University</li><li>City Government</li><li>County Public Safety</li><li>Regional Medical Center</li><li>US Armed Forces</li><li>Federal Employees</li></ul>',
    },
    'preferredemployerlist.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue:
        '<p>Don\'t see your employer? Contact our leasing team &mdash; new partnerships are added regularly.</p>',
    },
  },
};

(PreferredEmployerList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PreferredEmployerList;
